import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, BookOpen, Globe, HelpCircle } from 'lucide-react';
import { Avatar } from '../Profile/Avatar';
import { Username } from '../Profile/Username';
import { StatusIndicator } from '../StatusIndicator';
import { formatPrice } from '../../lib/utils/formatters';
import { formatCategoryName } from '../../lib/utils/formatters';
import type { Course } from '../../types/courses';

interface CourseCardProps {
  course: Course;
  showStatus?: boolean;
}

export function CourseCard({ course, showStatus = false }: CourseCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Calculate total duration from lessons if available
  const totalDuration = course.lessons?.reduce((acc, lesson) => acc + lesson.duration, 0) || 0;
  
  // Get lesson count from _count or lessons array
  const lessonCount = course._count?.lessons || course.lessons?.length || 0;

  const handleCardClick = () => {
    navigate(`/courses/${course.slug}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-background rounded-lg shadow-sm overflow-hidden hover:shadow-md active:scale-[0.98] active:shadow-none transition-all duration-150 cursor-pointer"
    >
      <div className="relative">
        <div className="aspect-w-16 aspect-h-9">
          <img
            src={course.thumbnail_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=400'}
            alt={course.title}
            className="w-full h-48 object-cover"
            loading="lazy"
          />
        </div>
        {course.isQuiz && (
          <div className="absolute top-3 left-3 bg-purple-600 text-white px-2 py-1 rounded-md text-xs font-medium flex items-center">
            <HelpCircle className="h-3 w-3 mr-1" />
            Quiz
          </div>
        )}
      </div>
      
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          {course.user && (
            <Link 
              to={`/profile/${course.user_id}/listings`}
              className="flex items-center space-x-2 group"
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar 
                url={course.user.avatar_url} 
                size="sm"
                userId={course.user_id}
                editable={false}
              />
              <Username 
                username={course.user.username || 'Anonymous'}
                userId={course.user_id}
                className="text-sm text-content group-hover:text-accent-text"
              />
            </Link>
          )}
          {showStatus && course.approval_status && (
            <StatusIndicator status={course.approval_status} />
          )}
        </div>

        <h3 className="text-lg font-semibold text-content mb-2">
          {course.title}
        </h3>

        <p className="text-content/80 line-clamp-2 mb-4">
          {course.description}
        </p>

        <div className="flex items-center justify-between text-sm text-content/60 mb-2">
          <div className="flex items-center space-x-4 flex-wrap">
            <span className="flex items-center">
              <BookOpen className="h-4 w-4 mr-1" />
              {lessonCount} {lessonCount === 1 ? t('common.lesson') : t('common.lessons')}
            </span>
            {totalDuration > 0 && (
              <span className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {Math.round(totalDuration / 60)} {t('common.min')}
              </span>
            )}
            <span className="flex items-center">
              <Globe className="h-4 w-4 mr-1" />
              {course.language.toUpperCase()}
            </span>
          </div>
          <span className="font-medium text-accent-text">
            {course.price > 0 ? formatPrice(course.price, course.currency) : t('common.free')}
          </span>
        </div>
        {course.category && (
          <div className="mt-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent-text/10 text-accent-text">
              {formatCategoryName(course.category)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
